import { Box, Flex, Text } from '@radix-ui/themes';
import { MetricHelper } from 'classes/helpers/metric.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { AnnotatedVideoPlayerControls } from 'components/common/video/annotated-player/controls';
import { CommonVideoPlayerHoC } from 'components/common/video/player';
import { DUMMY_DATA } from 'components/sections/admin-portal/machines/dialogs/settings/machine-shot-history/dummy-video';
import { VideoPlayerType } from 'enums/video-player.enums';
import { t } from 'i18next';
import { BallHelper } from 'lib_ts/classes/ball.helper';
import { IN_TO_CM } from 'lib_ts/classes/math.utilities';
import { PlateHelper } from 'lib_ts/classes/plate.helper';
import { ReplayCanvas } from 'lib_ts/classes/replay-canvas.helper';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { VideoHelper } from 'lib_ts/classes/video.helper';
import { ImageMime } from 'lib_ts/enums/files.enums';
import { StereoVideoType } from 'lib_ts/enums/videos.enums';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import { IHitter } from 'lib_ts/interfaces/i-hitter';
import { IStereoVideo } from 'lib_ts/interfaces/i-stereo-video';
import {
  IVideoPlayback,
  PLAYBACK_PLACEHOLDER,
} from 'lib_ts/interfaces/i-video';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';
import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { HittersService } from 'services/hitters.service';
import { MainService } from 'services/main.service';
import { ShotsService } from 'services/shots.service';
import { StereoVideosService } from 'services/stereo-videos.service';
import useMiscStore from 'stores/use-misc-store';
import videojs from 'video.js';
import './index.scss';

const DRAW_ORIGIN_DEBUG_DOT = false;
const USE_DUMMY_DATA = false;

// prefer annotated > replay videos
export const getFireVideoID = ({
  annotated_video_id,
  replay_video_id,
}: {
  annotated_video_id?: Nullable<string>;
  replay_video_id?: Nullable<string>;
}) => annotated_video_id || replay_video_id || undefined;

const getFrameIdx = (
  currentTimeSec: number,
  durationSec: number,
  totalFrames: number
) => {
  if (!durationSec) {
    return 0;
  }

  return Math.floor((currentTimeSec / durationSec) * totalFrames);
};

export interface IAnnotationDetails {
  // for sizing and origin metadata
  videoID: string;
  // for linking to live fires
  fireID?: Nullable<string>;
  // for linking to shot history and for the bs and traj
  shotID?: Nullable<string>;
  // for height > SZ sizing (empty fallback is the default SZ)
  hitterID?: Nullable<string>;
  // from the machine, if necessary
  isMetric?: boolean;
}

interface IProps extends videojs.PlayerOptions {
  details: IAnnotationDetails;
  onLoadUrl?: (url: string | undefined) => void;

  // mainly for positioning the multi-video-type segmented control from shot history
  extraControls?: ReactNode;
}

const OverlayData = (props: { label: string; value: string }) => (
  <Flex justify="between" gap="5">
    <Text size="3">{props.label}</Text>
    <Text size="3">{props.value}</Text>
  </Flex>
);

export const AnnotatedVideoPlayer: FC<IProps> = (props) => {
  const replaySettings = useMiscStore((s) => s.replaySettings);
  const { showDetails, showPlateLoc, showStrikeZone, showTracers } =
    replaySettings;

  // for strike zone
  const szCanvas = useRef<HTMLCanvasElement>(null);
  // for lines behind pitch and hit paths
  const tracerCanvas = useRef<HTMLCanvasElement>(null);
  // for box around current location of the ball
  const detectionCanvas = useRef<HTMLCanvasElement>(null);
  // for plate location circle once the pitch has reached the strike zone
  const plateCanvas = useRef<HTMLCanvasElement>(null);

  const [video, setVideo] = useState<IStereoVideo>();

  const [playback, setPlayback] = useState<IVideoPlayback>();

  const [shot, setShot] = useState<IMachineShot>();

  // load the shot
  useEffect(() => {
    if (!props.details.shotID) {
      setShot(undefined);
      return;
    }

    ShotsService.getInstance()
      .getById(props.details.shotID)
      .then((result) => setShot(result));
  }, [props.details.shotID]);

  const [hitter, setHitter] = useState<IHitter>();
  const szDef = useMemo(
    () => PlateHelper.getMlbAbsStrikeZone(hitter?.height_ft),
    [hitter?.height_ft]
  );

  // load the hitter
  useEffect(() => {
    if (!props.details.hitterID) {
      setHitter(undefined);
      return;
    }

    HittersService.getInstance()
      .getHitter(props.details.hitterID)
      .then((result) => setHitter(result));
  }, [props.details.hitterID]);

  // load the video
  useEffect(() => {
    if (USE_DUMMY_DATA) {
      setVideo(DUMMY_DATA);
      return;
    }

    StereoVideosService.getInstance()
      .getOne(props.details.videoID)
      .then((result) => setVideo(result));
  }, [props.details.videoID]);

  // load the playback
  useEffect(() => {
    if (!video?.path) {
      setPlayback(undefined);
      return;
    }

    MainService.getInstance()
      .signUrls([
        {
          region: video.region,
          path: video.path,
        },
      ])
      .then((result) => {
        const url = result[video.path]?.url;

        if (!url) {
          setPlayback(undefined);
          return;
        }

        const output: IVideoPlayback = {
          video: {
            url: url,
            mime_type: video.mime,
            speed: video.speed,
          },
          thumb: {
            url: '',
            mime_type: ImageMime.None,
          },
        };

        setPlayback(output);
      });
  }, [video?.path, video?.mime]);

  // notify the parent the URL loaded
  useEffect(() => {
    props.onLoadUrl?.(playback?.video.url);
  }, [playback?.video.url]);

  // ? draw the strike zone according to the current SZ of the hitter
  useEffect(() => {
    try {
      const canvas = szCanvas.current;
      if (!canvas) {
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (video?.type !== StereoVideoType.Replay) {
        // skip drawing any SZ for non-replay videos
        return;
      }

      if (!video?.metadata) {
        // skip drawing any SZ w/o origin
        return;
      }

      if (!VideoHelper.validOrigin(video.metadata)) {
        // skip drawing any SZ w/o valid origin
        return;
      }

      if (DRAW_ORIGIN_DEBUG_DOT) {
        const { origin_px } = video.metadata;
        ReplayCanvas.drawDebugDot(
          ctx,
          {
            x: origin_px.x,
            y: origin_px.y,
          },
          {
            color: '#FF00FF',
          }
        );
      }

      /** draw strike zone external box */
      const { origin_px, pixels_per_foot } = video.metadata;

      ReplayCanvas.drawStrikeZone(
        ctx,
        {
          x: pixels_per_foot.x,
          z: pixels_per_foot.z,
        },
        {
          // where (x=0',z=0') is in the video's pixel coordinates
          x: origin_px.x,
          y: origin_px.y,
        },
        szDef,
        {
          lineWidth: 1,
          color: 'rgb(255,255,255)',
        }
      );
    } catch (e) {
      console.error('drawStrikeZone effect', e);
    }
  }, [video?.type, video?.metadata, szDef]);

  // ? hides plate location before SZ arrival, shows it after
  const drawPlateLocAt = useCallback(
    (frameIdx: number) => {
      try {
        const canvas = plateCanvas.current;
        if (!canvas) {
          return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (!video) {
          return;
        }

        if (video.type !== StereoVideoType.Replay) {
          // skip drawing for non-replay videos
          return;
        }

        if (!video.metadata?.plate_px) {
          // we don't have a plate frame def
          return;
        }

        if (frameIdx < video.metadata.plate_px.ball_crossing.idx) {
          // ball has not arrived at plate yet
          return;
        }

        ReplayCanvas.drawPlateLoc(ctx, video.metadata.plate_px.ball_crossing, {
          lineWidth: 1,
          outlineColor: 'rgb(255,255,255)',
          fillColor: 'rgba(255,255,255,0.6)',
        });
      } catch (e) {
        console.error(`drawPlateLocAt ${frameIdx}`, e);
      }
    },
    [video]
  );

  // ? draws portion of each tracer for all frames up to the current frame
  const drawTracersAt = useCallback(
    (frameIdx: number) => {
      try {
        const canvas = tracerCanvas.current;
        if (!canvas) {
          return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        // reset the whole canvas to start
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (!video) {
          return;
        }

        if (video.type !== StereoVideoType.Replay) {
          // skip drawing for non-replay videos
          return;
        }

        if (
          video.metadata?.pitch_curve &&
          // ? don't draw the pitch curve on the final frame of the video
          frameIdx !== video.metadata.n_frames
        ) {
          const points = video.metadata.pitch_curve.filter(
            (m) => m.idx <= frameIdx
          );

          ReplayCanvas.drawTracerPoints(ctx, points, {
            lineWidth: 2,
            lineColor: 'rgb(255,141,204)',
            shadowWidth: 8,
            shadowColor: 'rgba(255,141,204,0.45)',
          });
        }

        if (video.metadata?.hit_curve) {
          const points = video.metadata.hit_curve.filter(
            (m) => m.idx <= frameIdx
          );

          ReplayCanvas.drawTracerPoints(ctx, points, {
            lineWidth: 2,
            lineColor: 'rgb(0,0,255)',
            shadowWidth: 8,
            shadowColor: 'rgba(0,0,255,0.45)',
          });
        }
      } catch (e) {
        console.error(`drawTracersAt ${frameIdx}`, e);
      }
    },
    [video]
  );

  // ? draws a box or ring around the ball if there's a detection for this particular frame
  const drawDetectionAt = useCallback(
    (frameIdx: number) => {
      try {
        const canvas = detectionCanvas.current;
        if (!canvas) {
          return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        // clear the previous box around the ball
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (!video) {
          return;
        }

        if (video.type !== StereoVideoType.Replay) {
          // skip drawing for non-replay videos
          return;
        }

        const detection = video.metadata?.detection_frames?.find(
          (m) => m.idx === frameIdx
        );

        if (!detection) {
          // there was nothing detected in this frame
          return;
        }

        ReplayCanvas.drawDetection(ctx, detection, {
          shape: 'circle',
          lineWidth: 1,
          color: 'rgb(255,255,255)',
        });
      } catch (e) {
        console.error(`drawDetectionAt ${frameIdx}`, e);
      }
    },
    [video]
  );

  const speedText = useMemo(() => {
    if (!shot?.traj) {
      return null;
    }

    const speedMph = TrajHelper.getSpeedFromTraj(shot.traj);
    const unit = MetricHelper.getSpeedUnit(props.details.isMetric);

    if (props.details.isMetric) {
      return `${MetricHelper.mphToKph(speedMph).toFixed(1)} ${unit}`;
    }

    return `${speedMph.toFixed(1)} ${unit}`;
  }, [props.details.isMetric, shot?.traj]);

  const breaksText = useMemo((): {
    hText: string;
    vText: string;
  } | null => {
    if (!shot?.traj) {
      return null;
    }

    const breaks = TrajHelper.getBreaks(shot.traj);
    const unit = MetricHelper.getBreaksUnit(props.details.isMetric);

    if (props.details.isMetric) {
      return {
        hText: `${(breaks.xInches * IN_TO_CM).toFixed(1)} ${unit}`,
        vText: `${(breaks.zInches * IN_TO_CM).toFixed(1)} ${unit}`,
      };
    }

    return {
      hText: `${breaks.xInches.toFixed(1)} ${unit}`,
      vText: `${breaks.zInches.toFixed(1)} ${unit}`,
    };
  }, [props.details.isMetric, shot?.traj]);

  const spinsText = useMemo((): {
    xText: string;
    yText: string;
    zText: string;
    netText: string;
  } | null => {
    if (!shot?.bs) {
      return null;
    }

    const netSpin = BallHelper.getNetSpin(shot.bs);
    const unit = 'rpm';

    return {
      xText: `${shot.bs.wx.toFixed(0)} ${unit}`,
      yText: `${shot.bs.wy.toFixed(0)} ${unit}`,
      zText: `${shot.bs.wz.toFixed(0)} ${unit}`,
      netText: `${netSpin.toFixed(0)} ${unit}`,
    };
  }, [shot?.bs]);

  // don't use any of the above for non-replay videos
  // if (video?.type !== StereoVideoType.Replay) {
  //   return (
  //     <CommonVideoPlayer
  //       {...props}
  //       playback={playback ?? PLAYBACK_PLACEHOLDER}
  //     />
  //   );
  // }

  return (
    <ErrorBoundary identifier="AnnotatedVideoPlayer">
      <div className="AnnotatedVideoPlayerWrapper">
        <CommonVideoPlayerHoC
          type={VideoPlayerType.Fire}
          {...props}
          // onTogglePlayback={(paused) => {
          //   if (paused) {
          //     return;
          //   }

          //   // clear the tracers when resuming playback
          //   drawTracersAt(-1);
          // }}
          // onPausedUpdate={(currentTimeSec, durationSec) => {
          //   // ? tracers are only shown while playback is paused
          //   if (!durationSec) {
          //     return;
          //   }

          //   if (!video.metadata?.n_frames) {
          //     return;
          //   }

          //   const frameIdx = getFrameIdx(
          //     currentTimeSec,
          //     durationSec,
          //     video.metadata.n_frames
          //   );

          //   drawTracersAt(frameIdx);
          // }}
          onContinuousUpdate={(currentTimeSec, durationSec) => {
            if (video?.type !== StereoVideoType.Replay) {
              return;
            }

            if (!durationSec) {
              return;
            }

            if (!video?.metadata?.n_frames) {
              return;
            }

            const frameIdx = getFrameIdx(
              currentTimeSec,
              durationSec,
              video.metadata.n_frames
            );

            drawTracersAt(frameIdx);
            drawPlateLocAt(frameIdx);
            drawDetectionAt(frameIdx);
          }}
          playback={playback ?? PLAYBACK_PLACEHOLDER}
        />

        {video?.type === StereoVideoType.Replay &&
          (shot?.traj || shot?.bs) &&
          showDetails && (
            <Flex direction="column" gap="1" className="Overlay">
              {speedText && (
                <OverlayData label={t('common.speed')} value={speedText} />
              )}
              {breaksText && (
                <OverlayData
                  label={t('common.v-break')}
                  value={breaksText.vText}
                />
              )}
              {breaksText && (
                <OverlayData
                  label={t('common.h-break')}
                  value={breaksText.hText}
                />
              )}
              {spinsText && (
                <OverlayData
                  label={t('common.spin-rate')}
                  value={spinsText.netText}
                />
              )}
              {spinsText && (
                <OverlayData
                  label={t('common.spin-x')}
                  value={spinsText.xText}
                />
              )}
              {spinsText && (
                <OverlayData
                  label={t('common.spin-y')}
                  value={spinsText.yText}
                />
              )}
              {spinsText && (
                <OverlayData
                  label={t('common.spin-z')}
                  value={spinsText.zText}
                />
              )}
            </Flex>
          )}

        <canvas
          id="sz-canvas"
          ref={szCanvas}
          className={
            video?.type === StereoVideoType.Replay && showStrikeZone
              ? undefined
              : 'hidden'
          }
          width={video?.width ?? 0}
          height={video?.height ?? 0}
        />
        <canvas
          id="tracer-canvas"
          ref={tracerCanvas}
          className={
            video?.type === StereoVideoType.Replay && showTracers
              ? undefined
              : 'hidden'
          }
          width={video?.width ?? 0}
          height={video?.height ?? 0}
        />
        <canvas
          id="detection-canvas"
          ref={detectionCanvas}
          className={
            video?.type === StereoVideoType.Replay && showTracers
              ? undefined
              : 'hidden'
          }
          width={video?.width ?? 0}
          height={video?.height ?? 0}
        />
        <canvas
          id="plate-canvas"
          ref={plateCanvas}
          className={
            video?.type === StereoVideoType.Replay && showPlateLoc
              ? undefined
              : 'hidden'
          }
          width={video?.width ?? 0}
          height={video?.height ?? 0}
        />

        {props.extraControls && (
          <Box className="ExtraControls">{props.extraControls}</Box>
        )}

        {video?.type === StereoVideoType.Replay && (
          <Box className="SettingsMenu">
            <AnnotatedVideoPlayerControls />
          </Box>
        )}
      </div>
    </ErrorBoundary>
  );
};
